import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserStore } from '../../../store/userStore';
import { useToast } from '../../../contexts';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import { UserIcon } from '@heroicons/react/24/solid';
import PasswordChangeModal from './PasswordChangeModal';

// /studyroom/* 접두사 사용 여부 (내부망/로컬만 true)
// 운영(app.newattend.com)/STG(stg.newattend.com)는 단순 /owner/* 경로 사용
const isStudyRoomStagingEnv = (): boolean => {
  const hostname = window.location.hostname;
  return (
    hostname === '192.168.0.38' ||
    hostname === '180.70.238.90' ||
    hostname === 'studyroom.staging.local' ||
    hostname === 'localhost' ||
    hostname.startsWith('192.168.')
  );
};

// 역할에 따른 대시보드 경로 반환
const getDashboardPath = (role: string): string => {
  const isStaging = isStudyRoomStagingEnv();
  if (role === 'admin') {
    return isStaging ? '/studyroom/admin/dashboard' : '/admin/dashboard';
  } else if (role === 'owner' || role === 'staff') {
    return isStaging ? '/studyroom/owner/dashboard' : '/owner/dashboard';
  } else if (role === 'hq_admin' || role === 'hq_staff') {
    // 본부 직원은 본부 시스템으로 (hq.newattend.com에서만 의미 있지만, 같은 도메인이면 임시로 owner dashboard)
    return isStaging ? '/studyroom/owner/dashboard' : '/owner/dashboard';
  }
  return isStaging ? '/studyroom/dashboard' : '/dashboard';
};

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useUserStore();
  const { showSuccess } = useToast();

  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  const typeParam = searchParams.get('type') || 'user';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      let success = false;
      let loginType = typeParam;

      if (loginType === 'admin') {
        throw new Error('관리자는 관리자 전용 페이지에서 로그인해주세요.');
      }

      try {
        success = await login(formData.phone, formData.password, loginType as 'user' | 'owner');
      } catch (firstError) {
        const otherType = loginType === 'user' ? 'owner' : 'user';

        try {
          success = await login(formData.phone, formData.password, otherType as 'user' | 'owner');
          loginType = otherType;
        } catch (err) {
          throw new Error('로그인에 실패했습니다.');
        }
      }

      if (success) {
        showSuccess('로그인했습니다.');

        const actualUser = useUserStore.getState().currentUser;
        const actualRole = actualUser?.role;

        if (actualUser?.first_login || actualUser?.temp_password || actualUser?.must_change_password) {
          setShowPasswordChange(true);
        } else {
          // SPA navigate (window.location.href는 Zustand persist hydration race condition 유발)
          setTimeout(() => {
            navigate(getDashboardPath(actualRole || 'user'), { replace: true });
          }, 500);
        }
      } else {
        setError('로그인에 실패했습니다. 전화번호/이메일과 비밀번호를 확인해주세요.');
      }
    } catch (err) {
      setError('로그인에 실패했습니다. 전화번호/이메일과 비밀번호를 확인해주세요.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 카카오 로그인 핸들러
  const handleKakaoLogin = async () => {
    setIsLoading(true);
    try {
      // 카카오 로그인 URL 가져오기
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://studyroom.newattend.com';
      const response = await fetch(`${baseUrl}/api/v1/kakao-auth/login-url`);

      if (!response.ok) {
        throw new Error('카카오 로그인 URL을 가져올 수 없습니다');
      }

      const data = await response.json();

      // 카카오 로그인 페이지로 이동
      window.location.href = data.url;
    } catch (error) {
      setError('카카오 로그인에 실패했습니다');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-10 h-10 bg-green-600 rounded-full">
              <AcademicCapIcon className="h-6 w-6 text-white" />
            </div>
            <h1 className="ml-3 text-2xl font-bold text-gray-900">StudyHub</h1>
          </div>
        </div>

        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          독서실 로그인
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          전화번호 또는 이메일로 로그인하세요.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                전화번호 또는 이메일
              </label>
              <div className="mt-1">
                <input
                  id="phone"
                  name="phone"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="010-1234-5678 또는 example@email.com"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                전화번호 또는 이메일로 로그인하세요. 처음 로그인 시 비밀번호는 전화번호 뒷 4자리입니다.
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                비밀번호
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="비밀번호를 입력하세요"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? '로그인 중...' : '로그인'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm text-green-600 hover:text-green-500"
              >
                홈페이지로 돌아가기
              </button>
            </div>
          </form>

          {/* 카카오 로그인 섹션 - 모바일 앱 개발 시 활성화 예정 */}
          {/* TODO: 학생/학부모용 모바일 앱에서 카카오 로그인 사용 */}
          {/*
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">또는</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleKakaoLogin}
                disabled={isLoading}
                className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-900 bg-yellow-400 hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#FEE500' }}
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3C6.477 3 2 6.477 2 10.5C2 13.067 3.667 15.433 6.333 16.9L5.5 20.5L9.5 18.5C10.3 18.667 11.133 18.75 12 18.75C17.523 18.75 22 15.273 22 11.25C22 7.227 17.523 3 12 3Z"
                    fill="#000000"
                  />
                </svg>
                카카오 로그인
              </button>
            </div>
          </div>
          */}

          <div className="mt-6 p-4 bg-blue-50 rounded-md border border-blue-200">
            <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
              <UserIcon className="h-4 w-4 mr-2" />
              회원 로그인
            </h3>
            <div className="text-xs text-blue-700 space-y-2">
              <div>
                <p className="font-medium">💳 독서실 회원</p>
                <p>전화번호로 로그인하세요</p>
              </div>
              <div>
                <p className="font-medium">🏢 독서실 사장님</p>
                <p>이메일 또는 전화번호로 로그인하세요</p>
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-green-50 rounded-md border border-green-200">
            <h3 className="text-sm font-medium text-green-800 mb-2">
              💡 이용 안내
            </h3>
            <div className="text-xs text-green-700 space-y-1">
              <p>• 처음 로그인하시면 비밀번호 변경이 필요합니다</p>
              <p>• 로그인 문제가 있으시면 독서실에 문의해주세요</p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              독서실을 운영하시나요?{' '}
              <button
                type="button"
                onClick={() =>
                  navigate(isStudyRoomStagingEnv() ? '/studyroom/owner/signup' : '/owner/signup')
                }
                className="font-medium text-green-600 hover:text-green-500 underline"
              >
                이용 신청하기
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* 비밀번호 변경 모달 */}
      <PasswordChangeModal
        isOpen={showPasswordChange}
        isFirstLogin={true}
        onSuccess={() => {
          setShowPasswordChange(false);
          const actualUser = useUserStore.getState().currentUser;
          const actualRole = actualUser?.role;
          navigate(getDashboardPath(actualRole || 'user'));
        }}
      />
    </div>
  );
};

export default LoginPage;
